import {
    Avatar,
    Badge,
    Box,
    Chip,
    Grid,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BLUEBORDER } from "../../../ui-components/CustomButton";
import { Primary, Secondary } from 'ui-components/Buttons';
import SectionCard from "ui-components/SectionCard";
import GradientIcon from "ui-components/icons/GradientIcon";
import StartupLogoPlaceholderIcon from "ui-components/icons/StartupLogoPlaceholderIcon";
import { useTheme } from "@emotion/react";
import { downloadFile, isVideoUrl } from "../../../utils";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { ChevronRight, Close, Download, InsertDriveFileOutlined, Language, MailOutline, PlayCircle } from "@mui/icons-material";
import { NotebookPen, Presentation, User, Users } from "lucide-react";
import indianFlag from "assets/indianFlag.png";
import PDFView from "../popups/PDFView";
import Cookies from 'js-cookie'
import { ChangeStatusForCurrentPipeline, FetchPipelinesStatuses, FetchPipelineStatus } from "api/startup_applications";
import { useToast } from 'context/ToastContext'
import CustomPopup from "ui-components/popups/CustomPopup";
import { Document, Page } from "react-pdf";
import { useWindowSize } from "ui-components/hooks";
import NoData from "ui-components/NoData";
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer';
import { useEngagementTracker } from "hooks/useEngagementTracking";
import { CONTACT_TARGETS, VIEW_TYPES } from "api/engagement";

// Presentation ported from the senior's startup-web
// (views/profile/profileSettings/ViewDetails.js) so the investor's view of a
// startup and the startup's own Backrr page look like the same page.
//
// Deliberately NOT ported, because they are owner-only affordances on a page an
// investor can only read: the Edit buttons, the dashed "Add Website / Add
// LinkedIn" contact slots, every "+ Add ..." link, ProfileNextSteps (profile
// completion), the "Preview as investor" toggle and KeyMetrics (dummy data
// there). Empty states keep startup-web's look but read from the investor's
// side instead of telling them to upload something.

const stripHtml = (html) =>
    String(html ?? "")
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();

// ---- Section heading (startup-web's SectionHeader, minus the Edit button) ----
const SectionHeader = ({ title }) => (
    <Box>
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb="0.75rem"
            sx={{ minHeight: "2.5rem" }}
        >
            <Typography sx={{ fontWeight: 700, fontSize: { xs: "1.05rem", md: "1.1rem" } }}>
                {title}
            </Typography>
        </Box>
        <Box
            sx={{
                borderBottom: (theme) => `1px solid ${theme.palette.customColors.gray300}`,
                mb: "1.25rem",
            }}
        />
    </Box>
);

// ---- Shared empty state: dashed box + gradient lucide icon ------------------
const EmptyState = ({ theme, icon, title, subtitle, minHeight = 180, maxWidth }) => (
    <Box
        sx={{
            display: "grid",
            placeItems: "center",
            gap: "0.75rem",
            minHeight,
            padding: "1.5rem 1rem",
            textAlign: "center",
            borderRadius: "12px",
            border: `1px dashed ${theme.palette.customColors.gray300}`,
        }}
    >
        <GradientIcon icon={icon} size={40} strokeWidth={1} />
        <Box>
            <Typography sx={{ fontWeight: 600, color: theme.palette.customColors.g300 }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography
                    sx={{
                        fontSize: "0.85rem",
                        color: theme.palette.customColors.g100,
                        mt: "0.25rem",
                        ...(maxWidth ? { maxWidth } : {}),
                    }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>
    </Box>
);

// ---- Founder row -----------------------------------------------------------
const PersonDetails = ({ theme, data, onLinkedinClick }) => {
    const hasLinkedin = Boolean(data?.linkedin_url);
    const openLinkedin = () => {
        if (hasLinkedin) {
            onLinkedinClick?.();
            const url = String(data?.linkedin_url);
            window.open(url.startsWith("http") ? url : "https://" + url, "_blank", "noopener,noreferrer");
        }
    };
    const isImageUploaded = data?.image && data?.image_name !== "undefined";
    // No src means MUI Avatar falls through to its child - the User icon below.
    const imageSrc = isImageUploaded
        ? data?.image + `?cacheBuster=${new Date().getTime()}`
        : undefined;
    const role = data?.title || data?.type;

    return (
        <Box
            onClick={hasLinkedin ? openLinkedin : undefined}
            sx={{
                width: "100%",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                gap: "1rem",
                padding: "1rem",
                border: `1px solid ${theme.palette.customColors.gray300}`,
                borderRadius: "12px",
                cursor: hasLinkedin ? "pointer" : "default",
                transition: "border-color 0.15s ease",
                "&:hover": hasLinkedin
                    ? { borderColor: theme.palette.customColors.indigo600 }
                    : {},
            }}
        >
            <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                badgeContent={
                    hasLinkedin ? (
                        <Box
                            sx={{
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                backgroundColor: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: `2px solid #fff`,
                                boxShadow: "0 1px 3px rgba(16,24,40,0.18)",
                            }}
                        >
                            <LinkedInIcon sx={{ fontSize: 16, color: theme.palette.customColors.indigo600 }} />
                        </Box>
                    ) : null
                }
            >
                <Avatar
                    src={imageSrc}
                    alt={data?.name || "Founder"}
                    sx={{
                        width: 56,
                        height: 56,
                        border: `1px solid ${theme.palette.customColors.gray300}`,
                        ...(isImageUploaded
                            ? {}
                            : { backgroundColor: "white", color: "black" }),
                    }}
                >
                    {/* With no src, MUI renders the child instead of the image. */}
                    <GradientIcon icon={User} size={24} />
                </Avatar>
            </Badge>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                {data?.name && (
                    <Typography sx={{ fontWeight: 700, fontSize: "1.125rem", lineHeight: 1.3 }}>
                        {data?.name}
                    </Typography>
                )}
                {role && (
                    <Typography
                        sx={{
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                            color: theme.palette.customColors.g300,
                            mt: "0.2rem",
                        }}
                    >
                        {role}
                    </Typography>
                )}
                {data?.bio && (
                    <Typography
                        sx={{
                            fontSize: "0.9rem",
                            color: theme.palette.customColors.g200,
                            mt: "0.4rem",
                        }}
                    >
                        {data?.bio}
                    </Typography>
                )}
            </Box>

            {hasLinkedin && (
                <ChevronRight
                    sx={{ color: theme.palette.customColors.g100, flexShrink: 0, alignSelf: "flex-start", mt: "0.15rem" }}
                />
            )}
        </Box>
    );
};

const RichTextDisplay = ({ htmlContent, color = "" }) => {
    const [open, setOpen] = React.useState(false);
    let content = open ? htmlContent : htmlContent?.substring(0, 600);
    return (
        <>
            {content && content?.length > 0 ? (
                <>
                    <div
                        dangerouslySetInnerHTML={{ __html: (htmlContent?.length > 600 && !open ? content + "..." : content) }}
                        className="rich-text"
                        style={{ color: color, lineBreak: "anywhere" }}
                    />
                    {htmlContent?.length > 600 && (
                        <BLUEBORDER
                            component="label"
                            role={undefined}
                            tabIndex={-1}
                            sx={{
                                backgroundColor: "transparent",
                                border: "0",
                                padding: "0",
                                color: "black",
                                fontWeight: 700,
                            }}
                            onClick={() => setOpen(!open)}
                        >
                            <div>{!open ? "See more" : "See less"}</div>
                        </BLUEBORDER>
                    )}
                </>
            ) : (
                "-"
            )}
        </>
    );
};

const DisplayDocuments = ({ type, url }) => {
    const theme = useTheme();
    const width = useWindowSize();
    const [numPages, setNumPages] = React.useState(1);
    const [pageWidth, setPageWidth] = React.useState(0);

    React.useEffect(() => {
        setPageWidth(document.getElementById('pdf_container')?.parentNode.clientWidth)
    }, [width])

    if (type === "pdf") {
        return <Document error={() => { return <NoData /> }} loading={<Box className="skeleton" height={"calc(100vh - 160px)"} width={"100%"}></Box>} file={url} onLoadSuccess={({ numPages }) => { setNumPages(numPages) }}>
            <div id='pdf_container3' style={{ backgroundColor: theme.palette.customColors.gray300, width: '100%' }}>
                {Array.apply(null, Array(numPages))
                    .map((x, i) => i + 1)
                    .map((page, index) => (
                        <div id='page_view' style={{ marginBottom: index != numPages - 1 ? '0.7rem' : 0 }}>
                            <Page
                                key={page}
                                pageNumber={page}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                scale={1}
                                loading={<Box className="skeleton" height={"calc(100vh - 160px)"} width={"100%"}></Box>}
                                width={pageWidth > 0 ? pageWidth : document.getElementById('pdf_container3')?.parentNode.clientWidth}
                            />
                        </div>
                    ))}
            </div>
        </Document>
    }
    else if (isVideoUrl(type)) {
        return <video src={url} width={"100%"} controls style={{
            borderRadius: '8px',
        }} ></video>
    }
    else {
        return <DocViewer documents={[{ uri: url, fileType: type }]} pluginRenderers={DocViewerRenderers} style={{ height: "100vh", minWidth: "100%", borderRadius: "8px" }} config={{ header: { disableHeader: true } }} />
    }
}

// Black 16:9-ish player shared by the Pitch and Product video cards. `actions`
// renders the buttons over the thumbnail and receives `play`.
const VideoPreview = ({ src, onPlay, actions }) => {
    const [isPlaying, setIsPlaying] = React.useState(false);
    const play = () => {
        onPlay?.();
        setIsPlaying(true);
    };

    return (
        <Box
            sx={{
                position: "relative",
                borderRadius: "20px",
                overflow: "hidden",
                width: "100%",
                height: 322,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                padding: "1.25rem",
                background: "#000",
            }}
        >
            {isPlaying && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 3,
                        backgroundColor: "#000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <video
                        src={src}
                        controls
                        autoPlay
                        controlsList="nodownload"
                        style={{ width: "100%", height: "100%", objectFit: "contain", backgroundColor: "#000" }}
                    />
                    <IconButton
                        onClick={() => setIsPlaying(false)}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: "#fff",
                            backgroundColor: "rgba(0,0,0,0.45)",
                            "&:hover": { backgroundColor: "rgba(0,0,0,0.65)" },
                        }}
                        aria-label="close video"
                    >
                        <Close sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
            )}

            <video
                src={`${src}#t=0.1`}
                preload="metadata"
                muted
                playsInline
                tabIndex={-1}
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    zIndex: 0,
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 0,
                    background:
                        "linear-gradient(180deg, rgba(20,10,40,0.45) 0%, rgba(20,10,40,0.15) 35%, rgba(20,10,40,0.70) 100%)",
                    pointerEvents: "none",
                }}
            />
            {actions(play)}
        </Box>
    );
};

const VideoSectionTitle = ({ title }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb="1.25rem">
        <Typography sx={{ fontWeight: 700, fontSize: { xs: "1.1rem", md: "1.25rem" } }}>
            {title}
        </Typography>
    </Box>
);

const WATCH_BUTTON_SX = {
    flex: 1,
    minWidth: 0,
    borderRadius: "12px",
    whiteSpace: "nowrap",
    px: { xs: "0.5rem", sm: "1rem" },
    fontSize: { xs: "0.8rem", sm: "0.875rem" },
    "& .MuiButton-startIcon": {
        marginRight: { xs: "0.25rem", sm: "0.5rem" },
    },
    "&.Mui-disabled": {
        backgroundColor: "#fff",
        color: "rgba(17,17,17,0.4)",
        border: "1px solid rgba(0,0,0,0.25)",
    },
};

// ---- Right column: Pitch + Founders (+ Pitch Deck when there is no video) ----
const PitchAndFounders = ({ founders, theme, startupDetail, trackEngagement }) => {
    const [currentRow, setCurrentRow] = React.useState(null);
    const [selectedPDF, setSelectedPDF] = React.useState("");

    const hasVideo = Boolean(startupDetail?.elevator_pitch_path);
    const hasProductVideo = Boolean(startupDetail?.product_video_path);
    const hasDeck = Boolean(startupDetail?.pitch_deck_path);

    // Unchanged from before this restyle: the deck opens in a new tab through the
    // `selected-file` cookie + ?view-document=true, which the popup below reads.
    // startup-web opens its own PDFView inline - that is its logic, not ours.
    // The single choke point for every "View Deck" affordance on this page, so
    // one call here covers the button, the thumbnail and the fallback.
    const handleOpenDocument = () => {
        trackEngagement?.(VIEW_TYPES.DECK_VIEW, "pitch_deck");

        window.open(
            `${window.location.origin}${window.location.pathname}?view-document=true`,
            "_blank"
        );

        Cookies.set(
            "selected-file",
            JSON.stringify({
                file_name: startupDetail?.startup_name ? `${startupDetail?.startup_name}` : "Pitch Deck",
                url: startupDetail?.pitch_deck_path,
            })
        );
    };

    const handleClosePDFViewer = () => {
        setSelectedPDF(null);
        setCurrentRow(null);
    };

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1.25rem", alignItems: "flex-start" }}>
            <PDFView
                open={Boolean(selectedPDF != null && selectedPDF?.length > 0)}
                pdfUrl={selectedPDF}
                handleClose={handleClosePDFViewer}
                currentRow={currentRow}
                title={
                    currentRow?.startup_name
                        ? "" + currentRow?.startup_name
                        : "Pitch Deck"
                }
            />

            {/* Pitch and Product video - shown when either video exists */}
            {(hasVideo || hasProductVideo) && (
                <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
                    {hasVideo && (
                        <>
                            <VideoSectionTitle title="Pitch" />
                            <VideoPreview
                                src={startupDetail?.elevator_pitch_path}
                                onPlay={() => trackEngagement?.(VIEW_TYPES.PITCH_REEL, "elevator_pitch")}
                                actions={(play) => (
                                    <Box sx={{ display: "flex", gap: "0.75rem", position: "relative", zIndex: 1 }}>
                                        <Secondary
                                            sx={WATCH_BUTTON_SX}
                                            startIcon={<PlayCircle />}
                                            disabled={!hasVideo}
                                            onClick={play}
                                        >
                                            Watch Video
                                        </Secondary>
                                        <Primary
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                height: "auto",
                                                borderRadius: "12px",
                                                whiteSpace: "nowrap",
                                                px: { xs: "0.5rem", sm: "1rem" },
                                                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                                                "& .MuiButton-startIcon": {
                                                    marginRight: { xs: "0.25rem", sm: "0.5rem" },
                                                },
                                            }}
                                            startIcon={<InsertDriveFileOutlined />}
                                            disabled={!hasDeck}
                                            onClick={handleOpenDocument}
                                        >
                                            View Deck
                                        </Primary>
                                    </Box>
                                )}
                            />
                        </>
                    )}

                    {hasProductVideo && (
                        <Box sx={{ mt: hasVideo ? "1.5rem" : 0 }}>
                            <VideoSectionTitle title="Product Video" />
                            <VideoPreview
                                src={startupDetail?.product_video_path}
                                onPlay={() => trackEngagement?.(VIEW_TYPES.PRODUCT_VIDEO, "product_video")}
                                actions={(play) => (
                                    <Box sx={{ display: "flex", position: "relative", zIndex: 1 }}>
                                        <Secondary sx={WATCH_BUTTON_SX} startIcon={<PlayCircle />} onClick={play}>
                                            Watch Video
                                        </Secondary>
                                    </Box>
                                )}
                            />
                        </Box>
                    )}
                </Box>
            )}

            {/* Founders + Pitch Deck column */}
            <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
                <SectionCard sx={{ padding: "1rem", marginTop: 0, width: "100%" }}>
                    <SectionHeader title="Founders" />
                    {founders?.length > 0 ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                            {founders?.map((person, index) => (
                                <PersonDetails
                                    theme={theme}
                                    data={person}
                                    key={index}
                                    onLinkedinClick={() =>
                                        trackEngagement?.(VIEW_TYPES.CONTACT_VIEW, CONTACT_TARGETS.FOUNDER_LINKEDIN)
                                    }
                                />
                            ))}
                        </Box>
                    ) : (
                        <EmptyState
                            theme={theme}
                            icon={Users}
                            title="No founders added yet"
                            subtitle="This startup hasn't added its founder details."
                            maxWidth={260}
                        />
                    )}
                </SectionCard>

                {/* Pitch Deck - only when there is NO video */}
                {!hasVideo && (
                    <SectionCard sx={{ padding: "1rem", marginTop: "1.25rem", width: "100%" }}>
                        <SectionHeader title="Pitch Deck" />
                        {hasDeck ? (
                            <Box
                                onClick={handleOpenDocument}
                                sx={{
                                    position: "relative",
                                    cursor: "pointer",
                                    borderRadius: "12px",
                                    overflow: "hidden",
                                    lineHeight: 0,
                                    border: `1px solid ${theme.palette.customColors.gray300}`,
                                }}
                            >
                                {startupDetail?.pitch_deck_thumbnail_path ? (
                                    <Box
                                        component="img"
                                        src={startupDetail?.pitch_deck_thumbnail_path}
                                        alt="Pitch Deck"
                                        sx={{
                                            width: "100%",
                                            display: "block",
                                            aspectRatio: "16 / 9",
                                            objectFit: "cover",
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            display: "grid",
                                            placeItems: "center",
                                            gap: "0.5rem",
                                            minHeight: 160,
                                            backgroundColor: theme.palette.customColors.inputField,
                                        }}
                                    >
                                        <InsertDriveFileOutlined sx={{ fontSize: 40, color: theme.palette.customColors.g100 }} />
                                        <Typography sx={{ fontWeight: 600, color: theme.palette.customColors.g300 }}>
                                            View Deck
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <EmptyState
                                theme={theme}
                                icon={Presentation}
                                title="No pitch deck uploaded"
                                subtitle="This startup hasn't uploaded a pitch deck yet."
                            />
                        )}
                    </SectionCard>
                )}
            </Box>
        </Box>
    );
};

const StartupInformation = ({ data: startupDetail, round_id }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const investor_id = Cookies.get("X-INV-ID") || null;
    let cookieStartupId = Cookies.get("startup_id");
    const trackEngagement = useEngagementTracker(cookieStartupId, investor_id);
    const [pipelineStatusesData, setPipelineStatusesData] = React.useState(null);
    const [selectedStartupStatus, setSelectedStartupStatus] = React.useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [openModal, setOpenModal] = React.useState(false);
    const selected_file = Cookies.get("selected-file") ? JSON.parse(Cookies.get("selected-file")) : null;

    React.useEffect(() => {
        if (searchParams.get("view-document") === "true") {
            setOpenModal(true);
        }
    }, [searchParams]);

    const handleClose = () => {
        setOpenModal(false);
        searchParams.delete("view-document");
        Cookies.remove("selected-file");
        setSearchParams(searchParams, { replace: true });
    };

    let {
        data: pipeline_details,
        isLoading: isLoadingStatus,
        refetch: refetchStartupStatus,
        status: pipeline_status
    } = FetchPipelineStatus({
        id: cookieStartupId, investor_id
    })

    React.useEffect(() => {
        if (pipeline_details) {
            setSelectedStartupStatus({ pipeline_id: pipeline_details?.pipeline_id, status: pipeline_details?.investor_status_id })
        }
    }, [pipeline_details])

    const statusTabsArray = !pipelineStatusesData?.data
        ? [

        ]
        : [
            ...pipelineStatusesData?.data,
        ];



    const { addToast } = useToast();
    const { mutateAsync: fetchStatusesData, isLoading: isLoadingStatuses } =
        FetchPipelinesStatuses({ investor_id: investor_id });

    const refetchStatusTabs = React.useCallback(() => {
        fetchStatusesData(
            { investor_id },
            {
                onSuccess: (response) => {
                    setPipelineStatusesData({
                        data:
                            response?.data?.data?.map((item) => {
                                return {
                                    ...item,
                                    mappedValue: item?.value,
                                    label: item?.status,
                                    value: item?._id,
                                    display_order: item?.display_order,
                                };
                            }) || [],
                    });
                },
                onError: (response) => {
                    // console.log(response, "response")
                },
            }
        );
    }, [investor_id, fetchStatusesData]);

    React.useEffect(() => {
        refetchStatusTabs();
    }, [investor_id]);

    const { isLoading: isChangingStatusForCurrentPipeline, mutateAsync: changeStatusForCurrentPipelineAsync,
    } = ChangeStatusForCurrentPipeline();

    const handleMoveToSelectedStatus = React.useCallback(
        (props) => {

            const constants = {
                investor: "investor",
                rejected: "rejected",
            };

            const isRejected =
                statusTabsArray.find(
                    (item) => item?.value === props?.payload?.investor_status_id
                )?.mappedValue === constants["rejected"];

            const mappedValue = statusTabsArray?.find(
                (item) => item?.value === props?.payload?.investor_status_id
            )?.mappedValue;

            const payload = {
                investor_status_id: props?.payload?.investor_status_id,
                ...(isRejected ? { rejected_by: constants["investor"] } : {}),
                ...(mappedValue ? { status: mappedValue } : {}),
            };

            changeStatusForCurrentPipelineAsync(
                { payload: payload, pipeline_id: props?.payload?.pipeline_id, matched: { investor_id, startup_id: props?.payload?.startup_id } },
                {
                    onSuccess: (successResponse) => {
                        if (successResponse?.data?.statusCode === 200) {
                            setSelectedStartupStatus({
                                ...pipeline_details,
                                status: props?.payload?.investor_status_id
                            })
                        }
                    },
                    onError: (errorResponse) => {
                        addToast(errorResponse?.response?.data?.message, "error");
                    },
                }
            );
        },
        [
            investor_id,
            pipeline_details?.pipeline_id,
            changeStatusForCurrentPipelineAsync,
            addToast,
        ]
    );

    const handleShortlist = () => {
        if (status != "interested")
            handleMoveToSelectedStatus({
                payload: {
                    investor_status_id: statusTabsArray?.find(item => item?.mappedValue === 'interested')?._id,
                    pipeline_id: selectedStartupStatus?.pipeline_id,
                    startup_id: cookieStartupId,
                    investor_id: investor_id
                }
            });
    }

    const handleReject = () => {
        if (status != "rejected")
            handleMoveToSelectedStatus({
                payload: {
                    investor_status_id: statusTabsArray?.find(item => item?.mappedValue === 'rejected')?._id,
                    pipeline_id: selectedStartupStatus?.pipeline_id,
                    startup_id: cookieStartupId,
                    investor_id: investor_id
                }
            });
    }

    let founders =
        startupDetail?.founder &&
            startupDetail?.founder != null &&
            startupDetail?.coFoundersArray
            ? [
                startupDetail?.founder,
                ...startupDetail?.coFoundersArray?.map((item) => {
                    return {
                        ...item,
                    };
                }),
            ]
            : [];

    const locationForHighlights = [
        startupDetail?.city,
        startupDetail?.state,
        startupDetail?.country
    ]
        .filter(Boolean)
        .join(', ') || '-';

    const companyHighlights = [
        {
            label: "LEGAL ENTITY NAME",
            value: startupDetail?.entity_name ? startupDetail?.entity_name : "-",
        },
        {
            label: "FORMATION DATE",
            value:
                startupDetail?.formation_date != null && startupDetail?.formation_date
                    ? startupDetail?.formatted_formation_date
                    : "-",
        },
        {
            label: "CURRENT STAGE",
            value: startupDetail?.stage_info?.name
                ? startupDetail?.stage_info?.name
                : "-",
        },
        {
            label: "SECTOR",
            value: `${startupDetail?.category_info?.name
                ? startupDetail?.category_info?.name
                : "-"
                } ${startupDetail?.sector_info &&
                    startupDetail?.sector_info?.length > 0 &&
                    startupDetail?.sector_info[0]?.sub_category
                    ? " (" + startupDetail?.sector_info[0]?.sub_category + ")"
                    : ""
                }`,
        },
        {
            label: "LOCATION",
            value: locationForHighlights,
        },
        {
            label: "BUSINESS MODEL",
            value: (
                <>
                    {startupDetail?.business_model_info &&
                        startupDetail?.business_model_info?.length > 0 ? (
                        startupDetail?.business_model_info?.length > 1 ? (
                            <Tooltip
                                title={startupDetail?.business_model_info?.map((item) => (
                                    <span>
                                        {" "}
                                        {item?.name} <br />
                                    </span>
                                ))}
                            >
                                <Chip
                                    sx={{ borderRadius: "4px" }}
                                    label={
                                        startupDetail?.business_model_info[0]?.name +
                                        " + " +
                                        String(startupDetail?.business_model_info?.length - 1)
                                    }
                                />
                            </Tooltip>
                        ) : (
                            startupDetail?.business_model_info[0]?.name
                        )
                    ) : (
                        "-"
                    )}
                </>
            ),
        },
    ];

    const location = [
        startupDetail?.city,
        startupDetail?.state,
        startupDetail?.country
    ]
        .filter(Boolean)
        .join(', ') || '-';

    const contactInformation = [
        {
            label: "Full Name",
            value: startupDetail?.first_name + " " + startupDetail?.last_name,
        },
        {
            label: "Mobile",
            value: `${startupDetail?.phone_code} - ${startupDetail?.mobile_number}`,
        },
        {
            label: "Location",
            value: location,
        },
        { label: "Email", value: `${startupDetail?.email}` },
        {
            label: "Website URL",
            value: (
                <Link
                    target="_blank"
                    to={"https://" + startupDetail?.website_url}
                    variant="body1"
                >
                    {startupDetail?.website_url}
                </Link>
            ),
        },
        {
            label: "Startup LinkedIn Page",
            value: (
                <Link
                    target="_blank"
                    to={"https://" + startupDetail?.linkedin_url}
                    variant="body1"
                >
                    {startupDetail?.linkedin_url}
                </Link>
            ),
        },
    ];


    const status = selectedStartupStatus?.status && Array.isArray(statusTabsArray) && statusTabsArray?.length > 0 ? statusTabsArray?.find(item => item?.value === selectedStartupStatus?.status)?.mappedValue : null;

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "1200px",
                    mx: "auto",
                    paddingBottom: "2rem",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                    gap: "1.25rem",
                }}
            >
                {/* LEFT COLUMN */}
                <Box sx={{ flex: "3 1 560px", minWidth: 0 }}>
                    <Grid container spacing={"1.25rem"} alignContent={"flex-start"}>
                        {/* Hero */}
                        <Grid item xs={12} sx={{ width: "100%" }}>
                            <Box
                                sx={{
                                    position: "relative",
                                    display: "flex",
                                    justifyContent: { xs: "center", sm: "space-between" },
                                    alignItems: "flex-start",
                                    gap: "1rem",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "center", sm: "flex-start" },
                                        gap: { xs: "1rem", sm: "1.5rem" },
                                    }}
                                >
                                    {/* Logo */}
                                    <Box
                                        sx={{
                                            flexShrink: 0,
                                            width: 108,
                                            height: 108,
                                            borderRadius: "6px",
                                            border: `1px solid ${theme.palette.customColors.gray300}`,
                                            backgroundColor: theme.palette.customColors.inputField,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {startupDetail?.startup_logo ? (
                                            <img
                                                src={
                                                    startupDetail?.startup_logo +
                                                    `?cacheBuster=${new Date().getTime()}`
                                                }
                                                alt="Startup Logo"
                                                draggable={false}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        ) : (
                                            <StartupLogoPlaceholderIcon size={64} />
                                        )}
                                    </Box>

                                    {/* Info */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: { xs: "center", sm: "flex-start" },
                                            gap: "0.75rem",
                                            flex: 1,
                                            minWidth: 0,
                                            width: "100%",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                flexWrap: "wrap",
                                                justifyContent: { xs: "center", sm: "flex-start" },
                                            }}
                                        >
                                            {startupDetail?.startup_name && (
                                                <Typography
                                                    sx={{
                                                        fontSize: { xs: "1.4rem", md: "1.75rem" },
                                                        fontWeight: 700,
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    {startupDetail?.startup_name}
                                                </Typography>
                                            )}
                                            {startupDetail?.country?.trim()?.length > 0 &&
                                                startupDetail?.country?.trim() === "India" && (
                                                    <img
                                                        src={indianFlag}
                                                        alt="India Flag"
                                                        style={{ width: "1.6rem", borderRadius: "2px" }}
                                                    />
                                                )}
                                        </Box>

                                        {/* Action buttons */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: "0.75rem",
                                                flexWrap: "wrap",
                                                justifyContent: { xs: "center", sm: "flex-start" },
                                                mt: "0.25rem",
                                            }}
                                        >
                                            {startupDetail?.website_url && (
                                                <Secondary
                                                    sx={{
                                                        borderRadius: "10px",
                                                        padding: "0.5rem 1rem",
                                                        fontSize: "0.875rem",
                                                        maxWidth: "100%",
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                        textAlign: "left",
                                                    }}
                                                    startIcon={<Language sx={{ fontSize: 18 }} />}
                                                    onClick={() => {
                                                        trackEngagement(VIEW_TYPES.CONTACT_VIEW, CONTACT_TARGETS.WEBSITE);
                                                        window.open("https://" + startupDetail?.website_url);
                                                    }}
                                                >
                                                    {startupDetail?.website_url}
                                                </Secondary>
                                            )}
                                            {startupDetail?.email && (
                                                <Secondary
                                                    sx={{
                                                        borderRadius: "10px",
                                                        padding: "0.5rem 1rem",
                                                        fontSize: "0.875rem",
                                                        maxWidth: "100%",
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                        textAlign: "left",
                                                    }}
                                                    startIcon={<MailOutline sx={{ fontSize: 18 }} />}
                                                    onClick={() => {
                                                        trackEngagement(VIEW_TYPES.CONTACT_VIEW, CONTACT_TARGETS.EMAIL);
                                                        window.open("mailto:" + startupDetail?.email);
                                                    }}
                                                >
                                                    Email
                                                </Secondary>
                                            )}
                                            {startupDetail?.linkedin_url && (
                                                <Secondary
                                                    sx={{
                                                        borderRadius: "10px",
                                                        padding: "0.5rem 1rem",
                                                        fontSize: "0.875rem",
                                                        maxWidth: "100%",
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                        textAlign: "left",
                                                    }}
                                                    startIcon={
                                                        <LinkedInIcon
                                                            sx={{ fontSize: 18, color: theme.palette.customColors.indigo600 }}
                                                        />
                                                    }
                                                    onClick={() => {
                                                        trackEngagement(VIEW_TYPES.CONTACT_VIEW, CONTACT_TARGETS.LINKEDIN);
                                                        window.open("https://" + startupDetail?.linkedin_url);
                                                    }}
                                                >
                                                    LinkedIn
                                                </Secondary>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Company Highlights */}
                        <Grid item xs={12} sx={{ width: "100%" }}>
                            <SectionCard>
                                <SectionHeader title="Company Highlights" />
                                <Grid container rowSpacing={3} columnSpacing={2}>
                                    {companyHighlights.map(({ label, value }, index) => (
                                        <Grid item xs={12} sm={6} lg={4} key={index}>
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: { xs: "0.85rem", sm: "0.90rem", md: "0.95rem" },
                                                        fontWeight: 600,
                                                        color: theme.palette.customColors.g100,
                                                        letterSpacing: "0.5px",
                                                        lineHeight: "100%",
                                                        marginBottom: "0.4rem",
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: { xs: "0.85rem", sm: "0.90rem", md: "0.95rem" },
                                                        lineHeight: "100%",
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {value}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </SectionCard>
                        </Grid>

                        {/* Problem Overview - startup-web gives this its own card; it used to
                            render here as a clamped tagline under the startup name. */}
                        <Grid item xs={12} sx={{ width: "100%" }}>
                            <SectionCard>
                                <SectionHeader title="Problem Overview" />
                                {stripHtml(startupDetail?.problem_solving) ? (
                                    <RichTextDisplay htmlContent={startupDetail?.problem_solving} />
                                ) : (
                                    <EmptyState
                                        theme={theme}
                                        icon={NotebookPen}
                                        title="No problem statement added yet"
                                        subtitle="This startup hasn't described the problem it solves."
                                        minHeight={140}
                                        maxWidth={320}
                                    />
                                )}
                            </SectionCard>
                        </Grid>

                        {/* Solution Overview */}
                        <Grid item xs={12} sx={{ width: "100%" }}>
                            <SectionCard>
                                <SectionHeader title="Solution Overview" />
                                {stripHtml(startupDetail?.description) ? (
                                    <RichTextDisplay htmlContent={startupDetail?.description} />
                                ) : (
                                    <EmptyState
                                        theme={theme}
                                        icon={NotebookPen}
                                        title="No overview added yet"
                                        subtitle="This startup hasn't added an overview yet."
                                        minHeight={140}
                                        maxWidth={320}
                                    />
                                )}
                            </SectionCard>
                        </Grid>
                    </Grid>
                </Box>

                {/* RIGHT COLUMN */}
                <Box sx={{ flex: "1 1 360px", minWidth: 280 }}>
                    <PitchAndFounders
                        founders={founders}
                        startupDetail={startupDetail}
                        trackEngagement={trackEngagement}
                        theme={theme}
                    />
                </Box>
            </Box>

            {Boolean(selected_file) && <CustomPopup open={openModal} handleClose={handleClose} title={
                <>
                    {selected_file?.file_name}
                    <Tooltip title="Download">
                        <IconButton
                            size="medium"
                            sx={{
                                position: 'absolute',
                                top: '0.5rem',
                                right: '3rem',
                            }}
                            onClick={(e) => {
                                downloadFile(selected_file?.url, selected_file?.file_name || (startupDetail?.startup_name || "Startup"));
                            }}
                            aria-label="more options"
                        >
                            <Download fontSize="medium" />
                        </IconButton>
                    </Tooltip>
                </>
            } titleSx={{
                marginBottom: { xs: '0rem', md: '2rem' },
                marginTop: { xs: '2rem', md: '0rem' },
            }} sx={{
                paddingTop: '0rem',
                placeContent: 'stretch',
                height: '100%'
            }} key={"File Popup"} >

                <DisplayDocuments type={selected_file?.url?.split('.')?.pop()?.toLowerCase()} url={selected_file?.url} />

            </CustomPopup>}
        </>
    );
};

export default StartupInformation;
